import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { useHandler } from "@redotech/react-util/hook";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { RedoFeatureNotificationCard } from "@redotech/redo-web/arbiter-components/feature-notification/redo-feature-notification-card";
import { memo, useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import * as styles from "./returns-nav-rearrangement-popup.module.css";

const LOCAL_STORAGE_KEY = "redo.returns-nav-rearrangement-dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
  } catch {
    // localStorage unavailable; fall back to in-memory dismissal.
  }
}

export const ReturnsNavRearrangementPopup = memo(
  function ReturnsNavRearrangementPopup() {
    const team = useContext(TeamContext);
    const user = useContext(UserContext);
    const { pathname } = useLocation();

    const [dismissed, setDismissed] = useState(() => readDismissed());

    const handleDismiss = useHandler(() => {
      setDismissed(true);
      writeDismissed();
    });

    if (dismissed) {
      return null;
    }
    if (isRedoEmailAddress(user?.email)) {
      return null;
    }
    if (!pathname.includes("/returns")) {
      return null;
    }
    if (!team) {
      return null;
    }

    const giftReceiptsEnabled = !!team.settings.returns?.enableGiftReceipts;
    const instantExchangeEnabled = !!team.settings.exchanges?.allowInstant;
    if (!giftReceiptsEnabled && !instantExchangeEnabled) {
      return null;
    }

    const movedItems = [
      giftReceiptsEnabled ? "Gift receipt requests" : null,
      instantExchangeEnabled ? "Instant exchange recovery" : null,
    ].filter((item): item is string => item !== null);

    const description = `We've moved ${movedItems.join(" and ")} into the Needs attention folder under Returns.`;

    return (
      <div className={styles.container}>
        <RedoFeatureNotificationCard
          actionText="Got it"
          description={description}
          onAction={handleDismiss}
          onDismiss={handleDismiss}
          title="We've tidied up the Returns nav"
        />
      </div>
    );
  },
);
